class ProductPersonalization extends HTMLElement {
  connectedCallback() {
    if (this.dataset.personalizationInit) return;
    this.dataset.personalizationInit = 'true';

    this.textInput = this.querySelector('[data-personalization-text]');
    this.fontSelect = this.querySelector('[data-personalization-font]');
    this.fontProperty = this.querySelector('[data-personalization-font-property]');
    this.fileInput = this.querySelector('[data-personalization-image]');
    this.fileButton = this.querySelector('[data-personalization-file-button]');
    this.fileDropzone = this.querySelector('[data-personalization-dropzone]');
    this.fileName = this.querySelector('[data-personalization-file-name]');
    this.errorMessage = this.querySelector('[data-personalization-error]');
    this.preview = this.querySelector('[data-personalization-preview]');
    this.previewText = this.querySelector('[data-personalization-preview-text]');
    this.previewImages = this.querySelector('[data-personalization-preview-images]');
    this.previewEmpty = this.querySelector('[data-personalization-preview-empty]');
    this.removeImageButton = this.querySelector('[data-personalization-remove-image]');
    this.objectUrls = [];
    this.renderedFileSignature = '';
    this.pickLock = false;

    this.textInput?.addEventListener('input', () => this.updatePreview());
    this.fontSelect?.addEventListener('change', () => this.updatePreview());
    this.fileInput?.addEventListener('change', () => this.handleFileChange());
    this.fileButton?.addEventListener('click', (event) => this.openFilePicker(event));
    this.removeImageButton?.addEventListener('click', () => this.clearFileInput(true));

    this.attachDropzone();
    this.updatePreview();
  }

  disconnectedCallback() {
    this.revokeObjectUrls();
  }

  openFilePicker(event) {
    event?.preventDefault();
    if (this.pickLock) return;

    this.pickLock = true;
    this.fileInput?.click();
    setTimeout(() => {
      this.pickLock = false;
    }, 700);
  }

  attachDropzone() {
    const dropzone = this.fileDropzone;
    if (!dropzone || !this.fileInput) return;

    dropzone.addEventListener('dragover', (event) => {
      event.preventDefault();
      dropzone.classList.add('is-dragover');
    });
    dropzone.addEventListener('dragleave', () => dropzone.classList.remove('is-dragover'));
    dropzone.addEventListener('drop', (event) => {
      event.preventDefault();
      dropzone.classList.remove('is-dragover');

      const files = event.dataTransfer?.files;
      if (!files?.length) return;

      const data = new DataTransfer();
      const allowMultiple = this.fileInput.hasAttribute('multiple');
      Array.from(files)
        .slice(0, allowMultiple ? files.length : 1)
        .forEach((file) => data.items.add(file));

      this.fileInput.files = data.files;
      this.fileInput.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  handleFileChange() {
    this.pickLock = false;

    const files = Array.from(this.fileInput?.files || []);
    if (files.length && !files.every((file) => this.fileIsAllowed(file))) {
      this.showError(`Invalid file type. Accepted: ${this.fileInput?.getAttribute('accept') || 'any'}`);
      this.clearFileInput(true);
      return;
    }

    this.hideError();
    this.updateFileName(files);
    this.updatePreview();
  }

  fileIsAllowed(file) {
    const acceptRaw = this.fileInput?.getAttribute('accept') || '';
    const rules = acceptRaw
      .split(',')
      .map((rule) => rule.trim())
      .filter(Boolean);

    if (!rules.length) return true;

    return rules.some((rule) => {
      if (rule.startsWith('.')) return file.name.toLowerCase().endsWith(rule.toLowerCase());

      const [type, subtype] = rule.toLowerCase().split('/');
      const [fileType, fileSubtype] = (file.type || '').toLowerCase().split('/');
      if (!type || !subtype || !fileType || !fileSubtype) return false;

      return (type === '*' || type === fileType) && (subtype === '*' || subtype === fileSubtype);
    });
  }

  updateFileName(files = Array.from(this.fileInput?.files || [])) {
    if (!this.fileName) return;

    if (files.length === 1) {
      this.fileName.textContent = files[0].name;
    } else if (files.length > 1) {
      this.fileName.textContent = `${files.length} files`;
    } else {
      this.fileName.textContent = this.dataset.fileEmptyText || 'No file chosen';
    }
  }

  updatePreview() {
    const text = (this.textInput?.value || '').trim();
    const selectedFont = this.fontSelect?.selectedOptions?.[0];
    const fontLabel = selectedFont?.textContent?.trim() || '';
    const fontFamily = selectedFont?.dataset.fontFamily || selectedFont?.value || '';
    const files = Array.from(this.fileInput?.files || []);
    const hasPreview = text !== '' || files.length > 0;

    if (this.fontProperty) {
      const nextValue = text && fontLabel ? fontLabel : '';
      const nextDisabled = !text || !fontLabel;
      const changed = this.fontProperty.value !== nextValue || this.fontProperty.disabled !== nextDisabled;

      this.fontProperty.value = nextValue;
      this.fontProperty.disabled = nextDisabled;

      if (changed) this.emitValueChange(this.fontProperty);
    }

    if (this.previewText) {
      this.previewText.textContent = text;
      this.previewText.hidden = text === '';
      if (fontFamily) this.previewText.style.fontFamily = fontFamily;
    }

    const fileSignature = this.getFileSignature(files);
    if (fileSignature !== this.renderedFileSignature) this.renderImagePreview(files, fileSignature);

    if (this.previewEmpty) this.previewEmpty.hidden = hasPreview;
    if (this.removeImageButton) this.removeImageButton.hidden = files.length === 0;
  }

  getFileSignature(files) {
    return files.map((file) => `${file.name}:${file.size}:${file.lastModified}:${file.type}`).join('|');
  }

  renderImagePreview(files, fileSignature = this.getFileSignature(files)) {
    if (!this.previewImages) return;

    this.revokeObjectUrls();
    this.previewImages.replaceChildren();
    this.previewImages.hidden = files.length === 0;
    this.renderedFileSignature = fileSignature;

    files.forEach((file) => {
      if (!file.type.startsWith('image/')) return;

      const url = URL.createObjectURL(file);
      this.objectUrls.push(url);

      const image = document.createElement('img');
      image.src = url;
      image.alt = file.name;
      image.loading = 'lazy';
      image.className = 'product-personalization__preview-image';
      this.previewImages.appendChild(image);
    });
  }

  clearFileInput(emit = false) {
    if (!this.fileInput) return;

    this.fileInput.value = '';
    this.updateFileName([]);
    this.updatePreview();

    if (emit) this.emitValueChange(this.fileInput);
  }

  clearValues() {
    if (this.textInput?.value) {
      this.textInput.value = '';
      this.emitValueChange(this.textInput);
    }

    if (this.fontSelect) {
      const before = this.fontSelect.value;
      this.fontSelect.selectedIndex = this.fontSelect.options.length ? 0 : -1;
      if (this.fontSelect.value !== before) this.emitValueChange(this.fontSelect);
    }

    this.clearFileInput(true);
    this.updatePreview();
  }

  showError(message) {
    if (!this.errorMessage) return;

    this.errorMessage.textContent = message;
    this.errorMessage.hidden = false;
  }

  hideError() {
    if (this.errorMessage) this.errorMessage.hidden = true;
  }

  emitValueChange(element) {
    element.dispatchEvent(new Event('input', { bubbles: true }));
    element.dispatchEvent(new Event('change', { bubbles: true }));
  }

  revokeObjectUrls() {
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url));
    this.objectUrls = [];
  }
}

if (!customElements.get('product-personalization')) {
  customElements.define('product-personalization', ProductPersonalization);
}

(function initConditionalPersonalization() {
  function parseList(str) {
    return (str || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => item.toLowerCase());
  }

  function readValue(form, key) {
    const escaped = window.CSS && CSS.escape ? CSS.escape(key) : key;
    const selector = `[name="properties[${escaped}]"]`;
    if (!form) return { found: false, value: '' };

    const all = Array.from(form.querySelectorAll(selector)).filter((element) => !element.disabled);
    if (!all.length) return { found: false, value: '' };

    if (all.some((element) => element.type === 'radio')) {
      const checked = all.find((element) => element.type === 'radio' && element.checked);
      return { found: true, value: checked?.value || '' };
    }

    if (all.some((element) => element.type === 'checkbox')) {
      const checked = all.find((element) => element.type === 'checkbox' && element.checked);
      if (checked) return { found: true, value: checked.value || '' };
    }

    const filled = all.find((element) => (element.value || '').trim() !== '');
    return { found: true, value: (filled || all[0]).value || '' };
  }

  function setDisabled(container, disabled) {
    container.querySelectorAll('input, select, textarea, button').forEach((element) => {
      if (disabled) {
        if (element.required) {
          element.setAttribute('data-was-required', 'true');
          element.required = false;
        }
        element.disabled = true;
      } else {
        element.disabled = false;
        if (element.getAttribute('data-was-required') === 'true') {
          element.required = true;
          element.removeAttribute('data-was-required');
        }
      }
    });
  }

  function evaluate(container) {
    const key = container.getAttribute('data-cond-key');
    if (!key) return;

    const operator = container.getAttribute('data-cond-op') || 'has_any_value';
    const values = parseList(container.getAttribute('data-cond-values'));
    const formId = container.getAttribute('data-form');
    const form = (formId && document.getElementById(formId)) || container.closest('form');
    const { found, value } = readValue(form, key);
    const normalizedValue = (value || '').trim().toLowerCase();

    let show = false;
    if (found) {
      if (operator === 'has_any_value') show = normalizedValue !== '';
      else if (operator === 'is_any_of') show = values.length ? values.includes(normalizedValue) : normalizedValue !== '';
      else if (operator === 'is_not_any_of') show = values.length ? !values.includes(normalizedValue) : true;
    }

    const isInitialLoad = !container.dataset.condInit;
    const wasVisible = container.classList.contains('is-visible') || container.getAttribute('aria-hidden') === 'false';

    if (show) {
      container.hidden = false;
      container.setAttribute('aria-hidden', 'false');
      if (!isInitialLoad) {
        requestAnimationFrame(() => container.classList.add('is-visible'));
      } else {
        container.classList.add('is-visible');
      }
    } else {
      if (!isInitialLoad && wasVisible && typeof container.clearValues === 'function') container.clearValues();
      container.classList.remove('is-visible');
      container.setAttribute('aria-hidden', 'true');

      if (!isInitialLoad) {
        setTimeout(() => {
          container.hidden = true;
        }, 300);
      } else {
        container.hidden = true;
      }
    }

    setDisabled(container, !show);
  }

  function attach(container) {
    if (!container || container.dataset.condInit) return;

    const key = container.getAttribute('data-cond-key');
    if (!key) return;

    const formId = container.getAttribute('data-form');
    const form = (formId && document.getElementById(formId)) || container.closest('form');
    const handler = () => evaluate(container);

    if (form) {
      const escaped = window.CSS && CSS.escape ? CSS.escape(key) : key;
      form.querySelectorAll(`[name="properties[${escaped}]"]`).forEach((element) => {
        element.addEventListener('change', handler);
        element.addEventListener('input', handler);
      });
    }

    evaluate(container);
    container.dataset.condInit = 'true';
  }

  function init(root = document) {
    root.querySelectorAll?.('product-personalization[data-cond-key]').forEach(attach);
  }

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
